import {useCallback, useEffect, useRef, useState} from 'react';

import {API_URL} from 'constant';
import {useNetwork} from 'hooks';

/** Re-mint a minute early so an in-flight request never lands on an expired token. */
const REFRESH_MARGIN_MS = 60 * 1000;
const FALLBACK_TTL_SECONDS = 900;

/**
 * Mint and keep fresh the short-lived `evault`-scoped token.
 *
 * esign.api cannot authenticate users — its bearer token is decoded but never
 * signature-verified, and it does not hold the session secret — so eVault endpoints take
 * a dedicated credential that clients.api issues from a verified session. Anything
 * embedding an eVault surface needs one:
 *
 * - the eVault drive page, and
 * - the send-envelope modal, whose `Vault this agreement` panel lists vaults.
 *
 * Shared rather than duplicated so the refresh logic exists once. Returns an empty token
 * until the first mint resolves; callers should hold off on rendering the surface until
 * it is set, or its first requests will 401.
 */
export const useVaultToken = () => {
	const [token, setToken] = useState('');
	const [error, setError] = useState('');
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const {post} = useNetwork({updateState: false});

	const mint = useCallback(async () => {
		const result = await post(API_URL.VAULT_TOKENS, {});
		const minted = result?.data?.token ?? result?.token;

		if (!minted) {
			setError('Could not start an eVault session.');
			return;
		}

		setError('');
		setToken(minted);

		const ttlMs =
			Number(result?.data?.expiresIn ?? FALLBACK_TTL_SECONDS) * 1000;
		timer.current = setTimeout(
			() => void mint(),
			Math.max(30_000, ttlMs - REFRESH_MARGIN_MS)
		);
	}, [post]);

	useEffect(() => {
		void mint();
		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {vaultToken: token, vaultTokenError: error, remint: mint};
};
